import React from 'react';
import {HStack, Text, Tooltip} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {TextProps} from '@chakra-ui/layout';
import {CategorieDeTarif} from 'api-types/tolede';
import {ModeTarifaireSouhaite} from 'api-types/ffconnect';
import {EtatPromo} from '@/hooks/usePromoLignePanier';

interface PrixTagProps {
  typePrix?: string;
  categorieDeTarif?: CategorieDeTarif;
  modeTarifaireSouhaite?: ModeTarifaireSouhaite;
  position?: 'absolute' | 'relative';
  styleProps?: TextProps;
  onClick?: () => void;
  /**
   * Promotion du catalogue, affichée en plus du tag de catégorie et sans permission requise.
   * `non_appliquee` => 'promotion' barré + tooltip : ne couvre pas la date de livraison du panier.
   */
  promo?: EtatPromo;
  /**
   * Libellé d'une catégorie qui n'en a pas de dédié : « Prix en cours » par défaut, pour le prix
   * courant du catalogue et du panier ; le code Tolede brut (`code`) pour un prix passé, comme
   * dans l'historique d'achat, où « Prix en cours » serait faux.
   */
  repliCategorieInconnue?: 'prix_en_cours' | 'code';
}

type RepliCategorieInconnue = NonNullable<
  PrixTagProps['repliCategorieInconnue']
>;

// Fonction pour obtenir les styles du tarif appliqué
const getTarifStyles = (
  tarif_applique?: CategorieDeTarif | null,
  modeTarifaire?: ModeTarifaireSouhaite,
): TextProps => {
  // Prix bloqué (dérogation) - cas de la prise de commande
  if (modeTarifaire === 'saisir_dero' || modeTarifaire === 'saisir_one_shot') {
    return {
      bg: 'yellow.warning',
      color: 'yellow.warning_text',
    };
  }

  if (!tarif_applique) return {};

  // Promotion (P)
  if (tarif_applique === 'P') {
    return {
      bg: 'transparent',
      color: 'red.fail',
      // _hover: {bg: 'red.fail_bg'}
    };
  }

  // Marché (M, N, S)
  if (
    tarif_applique === 'M' ||
    tarif_applique === 'N' ||
    tarif_applique === 'S'
  ) {
    return {
      bg: 'transparent',
      color: 'blue.main',
      // _hover: {bg:'blue.main_transparency_5'},
    };
  }

  // Dérogation (D), Prix bloqué, Prix saisi (V, et X sous le prix plancher)
  if (
    tarif_applique === 'D' ||
    tarif_applique === 'V' ||
    tarif_applique === 'X'
  ) {
    return {
      bg: 'transparent',
      color: 'yellow.warning_text',
      // _hover: {bg: 'yellow.warning'},
    };
  }

  // Tarif général (T), Dégagement (DGT)
  if (tarif_applique === 'T' || tarif_applique === 'DGT') {
    return {
      bg: 'transparent',
      color: 'gray.dark',
      // _hover: {bg: 'gray.bg'}
    };
  }

  // Prix métier (C)
  if (tarif_applique === 'C') {
    return {
      bg: 'transparent',
      color: 'green.prix_metier',
      // _hover: {bg: 'green.hover_prix_metier'}
    };
  }

  // Catégorie non transmise par Tolede (autre)
  if (tarif_applique === 'autre') {
    return {
      bg: 'transparent',
      color: 'gray.dark',
    };
  }

  return {};
};

// Fonction pour obtenir le libellé du tarif appliqué
const getTarifLabel = (
  t: (key: string) => string,
  tarif_applique?: CategorieDeTarif | null,
  modeTarifaire?: ModeTarifaireSouhaite,
  repliCategorieInconnue: RepliCategorieInconnue = 'prix_en_cours',
): string => {
  // Prix saisi (one-shot) - cas de la prise de commande
  if (modeTarifaire === 'saisir_one_shot') {
    return t('back_office.prise_de_commande.panier.prix_saisi');
  }

  // Prix bloqué (dérogation) - cas de la prise de commande
  if (modeTarifaire === 'saisir_dero') {
    return t('back_office.prise_de_commande.panier.prix_bloque_label');
  }

  if (!tarif_applique)
    return t('back_office.devis_personnalises.tarif_prix_en_cours');

  // Promotion (P)
  if (tarif_applique === 'P')
    return t('back_office.devis_personnalises.tarif_promotion');

  // Marché (M, N, S)
  if (
    tarif_applique === 'M' ||
    tarif_applique === 'N' ||
    tarif_applique === 'S'
  ) {
    return t('back_office.devis_personnalises.tarif_marche');
  }

  // Dérogation (D)
  if (tarif_applique === 'D')
    return t('back_office.devis_personnalises.tarif_derogation');

  // Prix saisi (V), y compris sous le prix plancher (X) : même libellé
  if (tarif_applique === 'V' || tarif_applique === 'X')
    return t('back_office.prise_de_commande.panier.prix_saisi');

  // Prix métier (C)
  if (tarif_applique === 'C')
    return t('back_office.devis_personnalises.tarif_metier');

  // Tarif général (T)
  if (tarif_applique === 'T')
    return t('back_office.devis_personnalises.tarif_general');

  // Dégagement (DGT)
  if (tarif_applique === 'DGT')
    return t('back_office.devis_personnalises.tarif_degagement');

  // Catégorie non transmise par Tolede (autre)
  if (tarif_applique === 'autre')
    return t('back_office.devis_personnalises.tarif_non_precise');

  // Catégorie sans libellé dédié : le code Tolede quand l'appelant le demande
  if (repliCategorieInconnue === 'code') return tarif_applique;

  return t('back_office.devis_personnalises.tarif_prix_en_cours');
};

// Conversion de l'ancien typePrix (NatureDuPrix) vers CategorieDeTarif
const natureDuPrixToCategorie = (
  typePrix?: string,
): CategorieDeTarif | undefined => {
  if (typePrix === 'promo') return 'P';
  if (typePrix === 'marche') return 'M';
  return undefined;
};

export const PrixTag: React.FC<PrixTagProps> = ({
  typePrix,
  categorieDeTarif: categorieDeTarifProp,
  modeTarifaireSouhaite,
  position = 'relative',
  styleProps = {},
  onClick,
  promo,
  repliCategorieInconnue,
}) => {
  const {t} = useTranslation();
  // `autre` : le serveur la pose quand Tolede n'a pas transmis la catégorie. Le type de prix la
  // précise quand il le peut (« Marché » couvre M, N et S) ; sinon le tag affiche « Non précisé ».
  const categorieDeTarif =
    categorieDeTarifProp === 'autre'
      ? (natureDuPrixToCategorie(typePrix) ?? 'autre')
      : (categorieDeTarifProp ?? natureDuPrixToCategorie(typePrix));

  // Un prix saisi ou bloqué par le commercial prime : son libellé est le seul affiché.
  const promoAffichee = modeTarifaireSouhaite ? undefined : promo;
  // La catégorie n'est pas répétée quand elle est déjà la promotion.
  const categorieAffichee =
    promoAffichee && categorieDeTarif === 'P' ? undefined : categorieDeTarif;

  if (!categorieAffichee && !modeTarifaireSouhaite && !promoAffichee) {
    return null;
  }

  const tag = (
    categorie: CategorieDeTarif | undefined,
    {barre}: {barre?: boolean} = {},
  ) => {
    const texte = (
      <Text
        p={1}
        ml={-1} // pour compenser visuelement le padding
        fontSize='10px'
        fontWeight='500'
        borderRadius='md'
        position={position}
        cursor={onClick ? 'pointer' : undefined}
        onClick={onClick}
        {...getTarifStyles(categorie, modeTarifaireSouhaite)}
        {...styleProps}
        textDecoration={barre ? 'line-through' : undefined}
      >
        {getTarifLabel(
          t,
          categorie,
          modeTarifaireSouhaite,
          repliCategorieInconnue,
        )}
      </Text>
    );

    // « Non précisé » : le tooltip dit au commercial que l'information manque côté Tolede.
    // Un prix saisi ou bloqué remplace ce libellé, le tooltip n'a alors plus lieu d'être.
    if (categorie !== 'autre' || modeTarifaireSouhaite) {
      return texte;
    }
    return (
      <Tooltip
        label={t('back_office.devis_personnalises.tarif_non_precise_tooltip')}
        placement='top'
      >
        {texte}
      </Tooltip>
    );
  };

  if (!promoAffichee) {
    return tag(categorieAffichee);
  }

  const tagPromo = (
    <Tooltip
      label={t('ligne_produit.promotion_non_applicable')}
      placement='top'
      isDisabled={promoAffichee === 'appliquee'}
    >
      {tag('P', {barre: promoAffichee === 'non_appliquee'})}
    </Tooltip>
  );

  // Promotion non applicable : la catégorie de tarif réelle est affichée juste à côté.
  return categorieAffichee ? (
    <HStack
      gap='2'
      flexWrap='wrap'
    >
      {tagPromo}
      {tag(categorieAffichee)}
    </HStack>
  ) : (
    tagPromo
  );
};
